import { useRef, useEffect, useState, forwardRef, useContext, useLayoutEffect } from 'react'
import { useSpring, animated } from 'react-spring'
import { Link } from 'react-router-dom'
import { Manager as PopperManager, Reference as PopperReference, Popper } from 'react-popper'

import { useStyles } from './executive-summary-page.style'
import { ReportBlock } from '../blocks/report-block'
import { utilService } from '../../common/services/util.service'
import { reportService } from '../report.service'
import { RouterContext } from '../../common/contexts/routerContext'
import { BlockActions } from '../blocks/block-actions'
import { useCommentContext } from '../../comments/context'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowDown } from '@fortawesome/pro-solid-svg-icons'

function HeaderComponent ({ report, title, description, showScrollArrow, onScrollArrowClick, highlightPageProperty }, ref) {
  const classes = useStyles({})

  const hasCommentAccess = reportService.hasCommentAccess(report)
  const {
    commentSidebarOpen,
    openPagePropertyComments
  } = useCommentContext() || {}
  const shouldEnableBlockCommenting = hasCommentAccess && commentSidebarOpen

  return (
    <div className={`${classes.headerSectionSafari} ${classes.headerSection}`} ref={ref}>
      <div className={classes.headerSectionContent}>
        <BlockActions
          blockKey="title"
          actions={{
            comment: {
              onClick () {
                openPagePropertyComments('title')
              }
            }
          }}
          enabled={shouldEnableBlockCommenting && highlightPageProperty !== 'title' }
          data-qa="page-title-actions"
        >
          <h1
            className={`${classes.headerTitle} ${highlightPageProperty === 'title' ? 'block-highlight' : ''}`}
            data-block-key="title"
            data-qa="page-title"
          >
            {title}
          </h1>
        </BlockActions>
        <BlockActions
          blockKey="description"
          actions={{
            comment: {
              onClick () {
                openPagePropertyComments('description')
              }
            }
          }}
          enabled={shouldEnableBlockCommenting && highlightPageProperty !== 'description' }
          data-qa="page-description-actions"
        >
          <p
            className={`${classes.headerDescription} ${highlightPageProperty === 'description' ? 'block-highlight' : ''}`}
            data-block-key="description"
            data-qa="page-description"
          >
            {description}
          </p>
        </BlockActions>
      </div>
      {showScrollArrow && (
        <div className={classes.arrowButtonContainer}>
          <button type="button" className={classes.arrowButton} onClick={onScrollArrowClick}>
            <div>Scroll down to continue</div>
            <FontAwesomeIcon color={'gray'} size={'xl'} className={`${classes.arrowButtonAnimate}`} icon={faArrowDown}/>
          </button>
        </div>
      )}
    </div>
  )
}
export const Header = forwardRef(HeaderComponent)

export const PageSection = function ({
  report,
  block,
  blockIndex,
  onMount,
  pageSection = {},
  containerHeight,
  currentBreakpoint,
  containerTopPadding,
  isLast,
  highlightBlock
}) {
  const ref = useRef()
  const clientHeight = ref && ref.current && ref.current.clientHeight
  const { key, type, dataKey, props, caption, methods } = block
  const classes = useStyles({
    renderType: pageSection.renderType,
    blockIndex,
    isLast
  })

  const hasCommentAccess = reportService.hasCommentAccess(report)
  const {
    commentSidebarOpen,
    openBlockComments
  } = useCommentContext() || {}
  const shouldEnableBlockCommenting = hasCommentAccess && commentSidebarOpen && !highlightBlock

  function getBlockWidth () {
    const blockWidthMap = {
      sunburst: {
        lg: 840,
        md: 840,
        sm: 768
      },
      default: {
        lg: 768,
        md: 640,
        sm: 512,
        default: 1024
      }
    }

    const breakpoint = currentBreakpoint || 'default'
    if (blockWidthMap[block.type] && blockWidthMap[block.type][breakpoint]) {
      return blockWidthMap[block.type][breakpoint]
    } else {
      return blockWidthMap.default[breakpoint]
    }
  }

  const getBlockSpecificProps = () => {
    const props = {}
    if (block.type === 'sunburst' && currentBreakpoint && currentBreakpoint !== 'lg') {
      props.legendPosition = 'left'
    }
    return props
  }

  function getDescriptionStyles () {
    const styles = { fontSize: 20, maxWidth: 360, height: clientHeight }
    if (currentBreakpoint === 'sm') {
      styles.fontSize = 16
      styles.maxWidth = 240
    } else if (currentBreakpoint) {
      styles.fontSize = 18
      styles.maxWidth = 300
    }
    return styles
  }

  useEffect(() => {
    if (ref && ref.current) {
      onMount(ref)
    }
  }, [clientHeight])

  const stickyPageOffset = containerTopPadding ? -1 * containerTopPadding : 0

  const sectionHeight = pageSection.renderType === 'sticky' ? containerHeight : (pageSection.height || 0)
  const blockPadding = 30

  return (
    <>
      <div
        className={classes.executiveSummarySection}
        data-block-key={block.key}
        style={{
          height: sectionHeight,
          top: pageSection.renderType === 'sticky' ? stickyPageOffset : (pageSection.offset || 0) + (pageSection.scrollableOffset || 0)
        }}>
        <div
          className={`${classes.blockContainer} ${highlightBlock ? 'block-highlight' : ''}`}
          ref={ref}
          style={{
            margin: block.description ? 0 : '0 auto'
          }}
        >
          <BlockActions
            blockKey={key}
            actions={{
              comment: {
                onClick () {
                  openBlockComments(key)
                }
              }
            }}
            enabled={shouldEnableBlockCommenting}
            style={{ width: getBlockWidth() + blockPadding }}
            data-qa={`${type}-block`}
          >
            <ReportBlock
              blockKey={key}
              title={block.title}
              type={type}
              dataKey={dataKey}
              props={props}
              report={report}
              caption={caption}
              methods={methods}
              width={getBlockWidth()}
              {...getBlockSpecificProps()}
            />
          </BlockActions>
          {/* Spacer element */}
          <div style={{ height: 30 }} />
        </div>
        {block.description && (
          <div className={classes.blockDescription} style={getDescriptionStyles()}>
            <p>
              {block.description}
            </p>
          </div>
        )}
        {isLast ? <ViewReportButton report={report} /> : null}
      </div>
    </>
  )
}

// This component is specifically meant to maintain
// blank divs that mimic the heights of page sections within
// the executive summary's scroll container. This helps provide
// a scrollable height for the overall container
let intersectionObserver
export const PageSectionPlaceholder = function ({ onIntersectEnter, onIntersectLeaveUp, pageSection = {}, containerRef }) {
  const ref = useRef()

  useLayoutEffect(() => {
    const currentRefHolder = ref.current
    // Create intersection observer to help detect when block should show/hide
    intersectionObserver = new IntersectionObserver(([{ isIntersecting, boundingClientRect }]) => {
      const currentY = boundingClientRect.y
      if (isIntersecting) {
        onIntersectEnter()
      }
      if (!isIntersecting && currentY > 0) {
        onIntersectLeaveUp()
      }
    }, {
      root: containerRef.current,
      rootMargin: `0px 0px -100% 0px`
    })

    intersectionObserver.observe(currentRefHolder)

    return () => {
      if (intersectionObserver && currentRefHolder) {
        intersectionObserver.unobserve(currentRefHolder)
      }
    }
  }, [])

  return (
    <div
      ref={ref}
      data-qa={`pageSection-${pageSection.key}`}
      style={{
        width: '100%',
        height: `${pageSection.scrollHeight}px`,
        marginTop: pageSection.marginTopOffset,
        position: 'relative'
      }}
    />
  )
}

let progressWindowResizeHandler
export const ProgressSidebar = function ({ container, blocks, scrollToSection, topContainerPadding }) {
  const classes = useStyles()
  const ref = useRef()
  const progressScrollTrackerRef = useRef()

  const [popoverShowing, setPopoverShowing] = useState()
  const [popoverHovered, setPopoverHovered] = useState()
  const [positionState, setPositionState] = useState('fixed')

  function progressScrollListener () {
    // Setting style here instead of setting a height state and causing tons
    // of re-renders whenever the user scrolls
    const scrollPercentage = container.scrollTop ? (container.clientHeight + container.scrollTop) / container.scrollHeight * 100 : 0
    progressScrollTrackerRef.current.style.height = `${scrollPercentage}%`
  }

  useEffect(() => {
    const calculateProgressBar = () => {
      const containerHeight = window.innerHeight - topContainerPadding
      const containerBarRatio = containerHeight / blocks.length

      if (containerBarRatio < 18) {
        setPositionState('simple')
      } else if (containerBarRatio < 26) {
        setPositionState('flexibleCondensed')
      } else if (containerBarRatio < 42) {
        setPositionState('flexible')
      } else {
        setPositionState('fixed')
      }
    }
    calculateProgressBar()

    // Handle window resizes to recalculate progress bar
    progressWindowResizeHandler = utilService.debounce(calculateProgressBar)

    if (ref && ref.current) {
      // Handle height changes on the document body in
      // order to set the positionState of the sidebar element
      window.addEventListener('resize', progressWindowResizeHandler)
      progressWindowResizeHandler() // Call once for initialization
      setTimeout(() => {
        if (ref && ref.current) {
          ref.current.style.opacity = 1
        }
      }, 100)
    }

    // Teardown
    return () => {
      window.removeEventListener('resize', progressWindowResizeHandler)
    }
  }, [ref && ref.current])

  useEffect(() => {
    if (container && positionState === 'simple') {
      container.addEventListener('scroll', progressScrollListener)
    } else if (container) {
      container.removeEventListener('scroll', progressScrollListener)
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', progressScrollListener)
      }
    }
  }, [container, positionState])

  const popoverAnimationProps = useSpring({ from: { opacity: 0 }, to: { opacity: 1 } })

  const scrollItemClickHandler = (i) => {
    scrollToSection(i, { behavior: 'auto' })
  }

  return blocks && blocks.length ? (
    <div className={`${classes.progressSidebarSafari} ${classes.progressSidebar} ${positionState}`} ref={ref}>
      <div className={classes.progressSidebarInner}>
        {blocks.map(({ key, title }, i) => (
          <div
            key={key}
            role="button"
            className={`
              button-${i}
              ${classes.progressSidebarItem}
            `}
            onClick={() => scrollItemClickHandler(i)}
          >
            <PopperManager>
              <PopperReference>
                {({ ref }) => (
                  <div
                    ref={ref}
                    className={classes.progressSidebarItemPopoverTrigger}
                    onMouseEnter={() => setPopoverShowing(i)}
                    onMouseLeave={() => setPopoverShowing()}
                  >
                    {(popoverShowing === i || popoverHovered === i) && (
                      <animated.div
                        style={popoverAnimationProps}
                        className={classes.progressSidebarPopoverContainer}
                        onMouseEnter={() => setPopoverHovered(i)}
                        onMouseLeave={() => setPopoverHovered()}
                      >
                        <Popper placement="left">
                          {({ ref, style, placement }) => (
                            <div ref={ref} style={style} data-placement={placement} className={classes.progressSidebarPopover}>
                              <div>
                                Section {i + 1}{title ? `: ${title}` : ''}
                              </div>
                            </div>
                          )}
                        </Popper>
                      </animated.div>
                    )}
                  </div>
                )}
              </PopperReference>
            </PopperManager>
          </div>
        ))}
        <div className={classes.progressSidebarLine}>
          <div ref={progressScrollTrackerRef} className={`${classes.progressSidebarActiveTracker} position-state-${positionState}`} style={positionState !== 'simple' ? { height: 0 } : {}} />
        </div>
      </div>
    </div>
  ) : null
}

export const ViewReportButton = function ({ report }) {
  const classes = useStyles({ reportLinkShowing: true })

  const routerContext = useContext(RouterContext)
  const parentVersion = routerContext.getQueryParam('parentVersion')

  return (
    report.actions.next
      ? (
        <div className={classes.reportLinkContainer} data-qa="view-full-report-button">
          <Link to={reportService.getPaginationLink({ report, parentVersion, type: 'next' })}>
          View full report
          </Link>
        </div>
      )
      : null
  )
}
